import { useEffect, useImperativeHandle, useRef, useState, useMemo, useCallback, useLayoutEffect } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { Environment, OrbitControls, ContactShadows } from '@react-three/drei';
import * as THREE from 'three';
import URDFLoader from 'urdf-loader';
import type { JointKey, JointAngles } from '../types';
import { getRobotModelConfig, findModelConfigByRobotModel } from '../config/robotModels1';
import type { RootTransform } from '../utils/simulationCsv';

// MuJoCo 类型
type MjModel = any; type MjData = any; type MujocoModule = any;

// ===== 棋盘格纹理 =====
const CC1 = '#334d66', CC2 = '#1a2633', CM = '#cccccc', CS = 512;
function makeCheckerTex(): THREE.CanvasTexture {
  const c = document.createElement('canvas'); c.width = c.height = CS;
  const x = c.getContext('2d')!, n = 10, s = CS / n, cl = s * 0.15;
  for (let r = 0; r < n; r++) for (let co = 0; co < n; co++)
    x.fillStyle = (r + co) % 2 === 0 ? CC1 : CC2, x.fillRect(co * s, r * s, s, s);
  x.strokeStyle = CM; x.lineWidth = 2;
  for (let r = 1; r < n; r += 2) for (let co = 1; co < n; co += 2) {
    const cx = co * s, cy = r * s;
    x.beginPath(); x.moveTo(cx - cl, cy); x.lineTo(cx + cl, cy);
    x.moveTo(cx, cy - cl); x.lineTo(cx, cy + cl); x.stroke();
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(4, 4); t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

// 地板 Y 坐标（MuJoCo z=0 → Three.js y=FLOOR_Y）
const FLOOR_Y = -0.75;
const DEBUG_DISABLE_GROUND_ALIGNMENT = false;

// ===== 主组件 =====
export default function MujocoViewer({
  currentFrame, simulationFrames, rootTransforms, jointKeyToUrdfName, controlsRef, onMjReady, tick, robotModel,
  playing, speed, playbackRange, onFrameChange, onPlaybackFrame, onGetCurrentFrameRef,
  onCanvasReady,
}: {
  onCanvasReady?: (canvas: HTMLCanvasElement) => void;
  currentFrame: number; simulationFrames: JointAngles[]; rootTransforms: RootTransform[];
  jointKeyToUrdfName: Partial<Record<JointKey, string>>;
  controlsRef: React.RefObject<any>;
  onMjReady?: (mj: { model: MjModel; data: MjData; M: MujocoModule; qmap: Record<string,number> }) => void;
  tick?: number;
  robotModel: string;
  playing?: boolean;
  speed?: number;
  playbackRange?: { start: number; end: number };
  onFrameChange?: (frame: number) => void;
  onPlaybackFrame?: (frame: number) => void;
  onGetCurrentFrameRef?: (getter: () => number) => void;
}) {
  const [status, setStatus] = useState<'loading'|'ready'|'error'>('loading');
  const [errMsg, setErrMsg] = useState('');
  const mjRef = useRef<{ model: MjModel; data: MjData; M: MujocoModule; qmap: Record<string, number> }|null>(null);
  const checkerTex = useMemo(() => makeCheckerTex(), []);
  const activeModelId = findModelConfigByRobotModel(robotModel)?.id ?? getRobotModelConfig().id;

  useEffect(() => {
    const cfg = findModelConfigByRobotModel(robotModel);
    const mujocoDir = cfg?.mujocoModelDir;
    if (!mujocoDir || !cfg?.mjModelPath) {
      setErrMsg(`当前机型 "${robotModel}" 不支持 MuJoCo 物理渲染`);
      setStatus('error');
      return;
    }
    setStatus('loading');
    setErrMsg('');
    mjRef.current = null;
    let ok = true;
    (async () => {
      try {
        const { default: init } = await import('@mujoco/mujoco');
        const M = await init();
        let xml: string;
        try {
          xml = await fetch(cfg.mjModelPath).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); });
        } catch {
          xml = await fetch(`/models/${mujocoDir}/scene.xml`).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); });
        }
        const meshMatches = [...xml.matchAll(/file="([^"]+\.STL)"/g)];
        const meshFiles = [...new Set(meshMatches.map(m => m[1]))];
        M.FS.mkdir('/meshes');
        const meshBase = cfg.mjModelPath.replace(/\/[^/]+$/, '/meshes');
        await Promise.all(meshFiles.map(async f => {
          const buf = await fetch(`${meshBase}/${f}`).then(r => r.arrayBuffer());
          M.FS.writeFile(`/meshes/${f}`, new Uint8Array(buf));
        }));
        console.log('[MujocoViewer] meshes preloaded:', meshFiles.length, 'dir:', mujocoDir);
        const model = M.MjModel.from_xml_string(xml);
        if (!model) throw new Error('from_xml_string failed');
        const data = new M.MjData(model);
        M.mj_forward(model, data);
        const qmap: Record<string, number> = {};
        if (!ok) return;
        console.log('[MujocoViewer] ready, joints:', Object.keys(qmap).length, 'qpos size:', model.nq);
        mjRef.current = { model, data, M, qmap };
        onMjReady?.(mjRef.current);
        setStatus('ready');
      } catch (e: any) { if (ok) { setErrMsg(e.message); setStatus('error'); } }
    })();
    return () => { ok = false; };
  }, [robotModel]);

  useEffect(() => {
    const mj = mjRef.current;
    if (!mj || status !== 'ready') return;
    const { model, data, M, qmap } = mj;
    const idx = Math.min(currentFrame, simulationFrames.length - 1);
    if (idx < 0 || !simulationFrames[idx]) return;
    const rt = rootTransforms[idx];
    if (rt) {
      // 查找 free joint (type=0) 的实际 qpos 地址
      let freeAddr = 0;
      for (let i = 0; i < model.njnt; i++) {
        if (model.jnt_type[i] === 0) { freeAddr = model.jnt_qposadr[i]; break; }
      }
      data.qpos[freeAddr + 0] = rt.pos[0]; data.qpos[freeAddr + 1] = rt.pos[1]; data.qpos[freeAddr + 2] = rt.pos[2];
      data.qpos[freeAddr + 3] = rt.quat[3]; data.qpos[freeAddr + 4] = rt.quat[0];
      data.qpos[freeAddr + 5] = rt.quat[1]; data.qpos[freeAddr + 6] = rt.quat[2];
      // [DIAG] 打印 MuJoCo qpos free joint 前7个值
      console.log('[DIAG qpos write] frame:', idx, 'freeAddr:', freeAddr,
        'pos:', data.qpos[freeAddr + 0].toFixed(4), data.qpos[freeAddr + 1].toFixed(4), data.qpos[freeAddr + 2].toFixed(4),
        'quat(wxyz):', data.qpos[freeAddr + 3].toFixed(4), data.qpos[freeAddr + 4].toFixed(4), data.qpos[freeAddr + 5].toFixed(4), data.qpos[freeAddr + 6].toFixed(4));
    }
    const frame = simulationFrames[idx];
    for (const jk of Object.keys(frame) as JointKey[]) {
      const urdfName = jointKeyToUrdfName[jk];
      if (!urdfName) continue;
      let addr = qmap[urdfName];
      if (addr === undefined) {
        const jid = M.mj_name2id(model, 3, urdfName);
        if (jid < 0) continue;
        addr = model.jnt_qposadr[jid] ?? -1;
        if (addr < 0) continue;
        qmap[urdfName] = addr;
      }
      data.qpos[addr] = frame[jk] * Math.PI / 180;
    }
  }, [currentFrame, simulationFrames, rootTransforms, jointKeyToUrdfName, status]);

  return (
    <div className="w-full h-full min-h-[420px] rounded-2xl overflow-hidden border border-white/10 bg-slate-950/40 relative">
      {status === 'loading' && <Overlay t="加载 MuJoCo 引擎中..." />}
      {status === 'error' && <Overlay t={errMsg} red />}
      <Canvas camera={{ position: [0, 0.25, 3], fov: 45 }} dpr={[1, 2]}
        gl={{ preserveDrawingBuffer: true }}
        onCreated={({ gl }) => { onCanvasReady?.(gl.domElement); }}
      >
        <ambientLight intensity={0.55} />
        <directionalLight position={[5, 6, 6]} intensity={1.1} />
        <directionalLight position={[-4, 2, 3]} intensity={0.45} color="#7c93ff" />
        <Environment preset="warehouse" />
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, FLOOR_Y, 0]} receiveShadow>
          <planeGeometry args={[40, 40]} />
          <meshStandardMaterial map={checkerTex} roughness={0.7} metalness={0} envMapIntensity={0.5} />
        </mesh>
        <ContactShadows position={[0, FLOOR_Y + 0.002, 0]} scale={5} far={1.8} blur={2.5} opacity={0.55} resolution={512} frames={1} />
          <UrdfRobot key={activeModelId} mjRef={mjRef} modelId={activeModelId} jointNameMap={jointKeyToUrdfName} currentFrame={currentFrame} simulationFrames={simulationFrames} rootTransforms={rootTransforms} tick={tick} playing={playing} speed={speed} playbackRange={playbackRange} onFrameChange={onFrameChange} onPlaybackFrame={onPlaybackFrame} onGetCurrentFrameRef={onGetCurrentFrameRef} />
        <FrontFacingOrbitControls
          controlsRef={controlsRef}
          modelId={activeModelId}
          rootTransform={rootTransforms[Math.min(currentFrame, rootTransforms.length - 1)]}
        />
      </Canvas>
      {/* <div className="absolute left-3 bottom-3 rounded-md bg-black/50 px-2 py-1 text-[10px] text-white/70 backdrop-blur-sm">左键旋转 · 中键缩放 · 右键平移</div> */}
    </div>
  );
}

function Overlay({ t, red }: { t: string; red?: boolean }) {
  return <div className="absolute inset-0 flex items-center justify-center bg-slate-950/80 z-10"><p className={`text-sm ${red?'text-red-400':'text-white/60'}`}>{t}</p></div>;
}

function FrontFacingOrbitControls({ controlsRef, modelId, rootTransform }: {
  controlsRef: React.RefObject<any>;
  modelId: string;
  rootTransform?: RootTransform;
}) {
  const orbitRef = useRef<any>(null);
  const viewRef = useRef({ modelId, rootTransform });
  const { camera } = useThree();
  viewRef.current = { modelId, rootTransform };

  useImperativeHandle(controlsRef, () => ({
    reset: () => {
      const controls = orbitRef.current;
      if (!controls) return;

      const {
        modelId: currentModelId,
        rootTransform: rt,
      } = viewRef.current;
      const cfg = getRobotModelConfig(currentModelId);
      const coordinateQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(
        cfg.defaultRotationX ?? 0,
        cfg.defaultRotationY ?? 0,
        cfg.defaultRotationZ ?? 0,
      ));
      const orientation = coordinateQuat.clone();

      if (rt) {
        const csvQuat = normalizedRootQuaternion(rt);
        if (csvQuat) {
          orientation.multiply(csvQuat);
        }
      }

      // 从机器人前方看向机器人
      const front = new THREE.Vector3(1, 0, 0).applyQuaternion(orientation);
      front.y = 0;
      if (front.lengthSq() < 1e-8) front.set(0, 0, 1);
      front.normalize();

      // MuJoCo 坐标系 → Three.js 坐标系：位置和朝向使用同一机型配置变换
      const px = Number(rt?.pos[0] ?? 0);
      const py = Number(rt?.pos[1] ?? 0);
      const pz = Number(rt?.pos[2] ?? 0);
      const target = new THREE.Vector3(px, py, pz).applyQuaternion(coordinateQuat);
      target.y = 0;
      camera.position.copy(target).addScaledVector(front, 3);
      camera.position.y = 0.25;
      controls.target.copy(target);
      controls.update();
      controls.saveState();
    },
  }), [camera, controlsRef]);

  return <OrbitControls ref={orbitRef} enablePan enableZoom minDistance={1.4} maxDistance={4} />;
}

// ===== URDF 模型（CSV 数据直驱，不经过 MuJoCo 物理求解器） =====
// 严格按 Version 2 (danceedit 2) 的工作逻辑，去掉内部播放和 BaseControl 编辑功能
type UrdfJoint = { setJointValue?: (...v: (number|null)[]) => boolean };
type MjHandle = { model: MjModel; data: MjData; M: MujocoModule; qmap: Record<string, number> };
type UrdfRuntime = { root: THREE.Object3D | null; joints: Record<string, UrdfJoint> };

// danceedit 内部以度存储关节角；URDFLoader 和 MuJoCo qpos 均要求弧度。
const jointAngleToRadians = (value: number) => THREE.MathUtils.degToRad(value);

function normalizedRootQuaternion(rootTransform: RootTransform | undefined): THREE.Quaternion | null {
  if (!rootTransform) return null;
  const qx = Number(rootTransform.quat[0] ?? 0);
  const qy = Number(rootTransform.quat[1] ?? 0);
  const qz = Number(rootTransform.quat[2] ?? 0);
  const qw = Number(rootTransform.quat[3] ?? 1);
  const qnorm = Math.hypot(qw, qx, qy, qz);
  if (!Number.isFinite(qnorm) || qnorm <= 1e-8) return null;
  return new THREE.Quaternion(qx / qnorm, qy / qnorm, qz / qnorm, qw / qnorm);
}

/**
 * K1/G1 共用的帧应用逻辑。
 * 必须先写根位姿，再写关节角，最后由调用方基于完整 FK 结果校准地面高度。
 */
function applyUrdfFrame(
  runtime: UrdfRuntime,
  frame: JointAngles,
  rootTransform: RootTransform | undefined,
  jointNameMap: Partial<Record<JointKey, string>>,
  defaultQuat: THREE.Quaternion,
) {
  const root = runtime.root;
  if (!root) return 0;

  let appliedJointCount = 0;
  root.position.set(0, 0, 0);
  root.quaternion.copy(defaultQuat);

  if (rootTransform) {
    const px = Number(rootTransform.pos[0] ?? 0);
    const py = Number(rootTransform.pos[1] ?? 0);
    const pz = Number(rootTransform.pos[2] ?? 0);

    // MuJoCo 坐标系 → Three.js 坐标系：位置和朝向使用同一机型配置变换
    root.position.copy(new THREE.Vector3(px, py, pz).applyQuaternion(defaultQuat));

    // 叠加 MuJoCo 绝对 root rotation
    const csvQuat = normalizedRootQuaternion(rootTransform);
    if (csvQuat) {
      root.quaternion.multiply(csvQuat);
    }

    // // [DIAG] 对比 rootTransform 原始值与 applyUrdfFrame 应用后的 root 位姿
    // console.log('[DIAG applyUrdfFrame] rootTransform.pos(xyz):',
    //   px.toFixed(4), py.toFixed(4), pz.toFixed(4),
    //   '| quat(xyzw):',
    //   rootTransform.quat[0].toFixed(4), rootTransform.quat[1].toFixed(4),
    //   rootTransform.quat[2].toFixed(4), rootTransform.quat[3].toFixed(4));
    // console.log('[DIAG applyUrdfFrame] → root.position(xyz):',
    //   root.position.x.toFixed(4), root.position.y.toFixed(4), root.position.z.toFixed(4),
    //   '| root.quat(xyzw):',
    //   root.quaternion.x.toFixed(4), root.quaternion.y.toFixed(4),
    //   root.quaternion.z.toFixed(4), root.quaternion.w.toFixed(4));
  }

  for (const jointKey of Object.keys(jointNameMap) as JointKey[]) {
    const urdfName = jointNameMap[jointKey];
    if (!urdfName) continue;
    const joint = runtime.joints[urdfName];
    if (!joint?.setJointValue) continue;
    const value = Number(frame[jointKey]);
    if (Number.isFinite(value)) {
      joint.setJointValue(jointAngleToRadians(value));
      appliedJointCount += 1;
    }
  }
  return appliedJointCount;
}

function alignUrdfToFloor(
  runtime: UrdfRuntime,
  group: THREE.Group | null,
  currentOffset: number | null,
): number | null {
  if (!runtime.root || !group || runtime.root.parent !== group) return currentOffset;

  group.position.set(0, FLOOR_Y, 0);
  let offset = currentOffset;
  if (offset === null) {
    group.updateWorldMatrix(true, true);
    const bounds = new THREE.Box3().setFromObject(runtime.root);
    if (Number.isFinite(bounds.min.y)) {
      offset = FLOOR_Y - bounds.min.y;
    }
  }
  if (offset !== null) {
    group.position.y = FLOOR_Y + offset;
  }
  return offset;
}

function UrdfRobot({ mjRef, modelId, jointNameMap, currentFrame, simulationFrames, rootTransforms, playing, speed, playbackRange, onFrameChange, onPlaybackFrame, onGetCurrentFrameRef }: {
  mjRef: React.MutableRefObject<MjHandle | null>;
  modelId: string; jointNameMap: Partial<Record<JointKey, string>>;
  currentFrame: number; simulationFrames: JointAngles[]; rootTransforms: RootTransform[];
  tick?: number;
  playing?: boolean;
  speed?: number;
  playbackRange?: { start: number; end: number };
  onFrameChange?: (frame: number) => void;
  onPlaybackFrame?: (frame: number) => void;
  onGetCurrentFrameRef?: (getter: () => number) => void;
}) {
  const FPS = 30;
  // 播放状态：内部帧计数器，播放时不受 React currentFrame prop 驱动
  const playbackFrameRef = useRef(currentFrame);
  const accumDeltaRef = useRef(0);
  // 暴露当前帧号给父组件
  useEffect(() => {
    onGetCurrentFrameRef?.(() => playbackFrameRef.current);
  }, [onGetCurrentFrameRef]);
  // 非播放时同步 React currentFrame 到内部计数器
  if (!playing) {
    playbackFrameRef.current = currentFrame;
  }
  const groupRef = useRef<THREE.Group>(null);
  const rRef = useRef<UrdfRuntime>({ root: null, joints: {} });
  const [root, setRoot] = useState<THREE.Object3D | null>(null);
  const cfg = getRobotModelConfig(modelId);

  // 预计算默认四元数（坐标系转换：MuJoCo Z-up → Three.js Y-up）
  const defaultQuat = useMemo(
    () => new THREE.Quaternion().setFromEuler(
      new THREE.Euler(cfg.defaultRotationX ?? 0, cfg.defaultRotationY ?? 0, cfg.defaultRotationZ ?? 0),
    ),
    [cfg.defaultRotationX, cfg.defaultRotationY, cfg.defaultRotationZ],
  );

  // 缓存 jointNameMap 的 key 列表
  const jointKeys = useMemo(() => Object.keys(jointNameMap) as JointKey[], [jointNameMap]);

  // 记录最后实际应用到画面的帧号，避免重复计算
  const emptyCache = () => ({
    frame: -1,
    frames: null as JointAngles[] | null,
    roots: null as RootTransform[] | null,
    runtime: null as UrdfRuntime | null,
    jointMap: null as Partial<Record<JointKey, string>> | null,
  });
  const lastAppliedRef = useRef(emptyCache());
  const pendingInitialCommitRef = useRef(false);
  // 首帧关节姿态应用后计算一次视觉脚底偏移，避免加载瞬间脚面陷入地板。
  const groundOffsetRef = useRef<number | null>(null);

  const { invalidate: invalidateScene } = useThree();

  // 所有非播放和播放更新都经过同一条提交链路，避免首帧被不同 effect 覆盖。
  const commitUrdfFrame = useCallback((idx: number, force = false) => {
    if (!Number.isInteger(idx) || idx < 0 || idx >= simulationFrames.length) return false;
    const runtime = rRef.current;
    const group = groupRef.current;
    const frame = simulationFrames[idx];
    if (!runtime.root || !group || runtime.root.parent !== group || !frame) return false;
    const cacheMatches = lastAppliedRef.current.frame === idx &&
      lastAppliedRef.current.frames === simulationFrames &&
      lastAppliedRef.current.roots === rootTransforms &&
      lastAppliedRef.current.runtime === runtime &&
      lastAppliedRef.current.jointMap === jointNameMap;
    if (!force && cacheMatches) return false;

    const appliedJointCount = applyUrdfFrame(runtime, frame, rootTransforms[idx], jointNameMap, defaultQuat);
    runtime.root.updateWorldMatrix(true, true);
    if (!DEBUG_DISABLE_GROUND_ALIGNMENT) {
      groundOffsetRef.current = alignUrdfToFloor(runtime, group, groundOffsetRef.current);
    }
    group.updateWorldMatrix(true, true);
    lastAppliedRef.current = { frame: idx, frames: simulationFrames, roots: rootTransforms, runtime, jointMap: jointNameMap };
    invalidateScene();

    return true;
  }, [simulationFrames, rootTransforms, jointNameMap, defaultQuat, invalidateScene]);

  // 模型变更时重新计算地面偏移；帧编辑不会改变模型身份。
  useEffect(() => {
    groundOffsetRef.current = null;
    lastAppliedRef.current = emptyCache();
  }, [cfg.urdfPath, cfg.meshRoot, cfg.defaultScale, defaultQuat]);

  // 加载 URDF
  useEffect(() => {
    let disposed = false;
    const loader = new URDFLoader();
    loader.packages = cfg.meshRoot;
    loader.fetchOptions = { mode: 'cors' };

    loader.load(cfg.urdfPath, (loaded: any) => {
      // 模型切换时旧的 K1/G1 请求可能晚于新请求返回，禁止旧回调覆盖当前模型。
      if (disposed) return;

      loaded.scale.setScalar(cfg.defaultScale ?? 1);
      loaded.position.set(0, 0, 0);
      loaded.quaternion.copy(defaultQuat);

      const joints: Record<string, UrdfJoint> = {};
      if (loaded.joints) {
        for (const [name, joint] of Object.entries<UrdfJoint>(loaded.joints)) {
          joints[name] = joint;
        }
      }

      rRef.current = { root: loaded, joints };
      setRoot(loaded);

      console.log('[UrdfRobot] URDF loaded:', cfg.urdfPath, 'joints:', Object.keys(joints).length);
    }, undefined, (err: unknown) => {
      if (disposed) return;
      console.error('[UrdfRobot] URDF load failed:', cfg.urdfPath, err);
    });

    return () => {
      disposed = true;
    };
  }, [cfg.urdfPath, cfg.meshRoot, cfg.defaultScale, defaultQuat]);

  // root 只在挂载阶段进入场景，首帧姿态由后续 R3F useFrame 强制提交。
  const { invalidate: invalidateOnMount } = useThree();
  useLayoutEffect(() => {
    const group = groupRef.current;
    if (!group || !root) return;
    group.add(root);
    root.visible = false;
    pendingInitialCommitRef.current = true;
    lastAppliedRef.current = emptyCache();
    invalidateOnMount();
    return () => { if (root.parent === group) group.remove(root); };
  }, [root, invalidateOnMount]);

  // 每帧：从 CSV 数据设置 URDF 关节角度和基座位姿
  useFrame((_state, delta) => {
    const r = rRef.current;
    if (!r.root) return;

    // 播放时：基于 delta 时间推进内部帧计数器
    if (playing && simulationFrames.length > 0) {
      const spd = speed ?? 1;
      accumDeltaRef.current += delta * spd;
      const framesToAdvance = Math.floor(accumDeltaRef.current * FPS);
      if (framesToAdvance > 0) {
        accumDeltaRef.current -= framesToAdvance / FPS;
        const range = playbackRange ?? { start: 0, end: simulationFrames.length - 1 };
        let next = playbackFrameRef.current + framesToAdvance;
        if (next > range.end) {
          // 循环：超出范围后回到 start
          next = range.start + ((next - range.start) % (range.end - range.start + 1));
        }
        playbackFrameRef.current = next;
        onFrameChange?.(next);
        onPlaybackFrame?.(next);
      }
    }

    if (pendingInitialCommitRef.current) {
      const idx = Math.max(0, Math.min(currentFrame, simulationFrames.length - 1));
      const committed = commitUrdfFrame(idx, true);
      if (committed) {
        pendingInitialCommitRef.current = false;
        if (rRef.current.root) rRef.current.root.visible = true;
        invalidateScene();
      }
      return;
    }

    const effectiveFrame = playing ? playbackFrameRef.current : currentFrame;
    const idx = Math.min(effectiveFrame, simulationFrames.length - 1);
    if (idx < 0 || !simulationFrames[idx]) return;

    // 帧无变化时跳过；实际提交统一由 commitUrdfFrame 完成。
    commitUrdfFrame(idx, false);

    const frame = simulationFrames[idx];
    const rt = rootTransforms[idx];

    // 同步 MuJoCo qpos（用于 BaseControl 面板等），但不调用 mj_forward
    const mj = mjRef.current;
    if (mj && rt) {
      const { data, model, M, qmap } = mj;
      // 查找 free joint (type=0) 的实际 qpos 地址
      let freeAddr = 0;
      for (let i = 0; i < model.njnt; i++) {
        if (model.jnt_type[i] === 0) { freeAddr = model.jnt_qposadr[i]; break; }
      }
      data.qpos[freeAddr + 0] = Number(rt.pos[0] ?? 0);
      data.qpos[freeAddr + 1] = Number(rt.pos[1] ?? 0);
      data.qpos[freeAddr + 2] = Number(rt.pos[2] ?? 0);
      const qx = Number(rt.quat[0] ?? 0);
      const qy = Number(rt.quat[1] ?? 0);
      const qz = Number(rt.quat[2] ?? 0);
      const qw = Number(rt.quat[3] ?? 1);
      const qnorm = Math.hypot(qw, qx, qy, qz);
      if (Number.isFinite(qnorm) && qnorm > 1e-8) {
        data.qpos[freeAddr + 3] = qw / qnorm;
        data.qpos[freeAddr + 4] = qx / qnorm;
        data.qpos[freeAddr + 5] = qy / qnorm;
        data.qpos[freeAddr + 6] = qz / qnorm;
      } else {
        data.qpos[freeAddr + 3] = 1;
        data.qpos[freeAddr + 4] = 0;
        data.qpos[freeAddr + 5] = 0;
        data.qpos[freeAddr + 6] = 0;
      }

      for (let i = 0; i < jointKeys.length; i++) {
        const jk = jointKeys[i];
        const urdfName = jointNameMap[jk];
        if (!urdfName) continue;
        let jaddr = qmap[urdfName];
        if (jaddr === undefined) {
          const jid = M.mj_name2id(model, 3, urdfName);
          if (jid < 0) continue;
          jaddr = model.jnt_qposadr[jid] ?? -1;
          if (jaddr < 0) continue;
          qmap[urdfName] = jaddr;
        }
        const val = Number(frame[jk]);
        if (Number.isFinite(val)) {
          data.qpos[jaddr] = jointAngleToRadians(val);
        }
      }
    }
  });

  return <group ref={groupRef} />;
}
